"use client";

import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import {
  Project,
  RemoteEndpoint,
  CopilotCloudPublicApiKey,
  CopilotCloudPrivateApiKey,
  ProviderApiKey,
} from "@prisma/client";
import { ExtendedUser } from "@/server/actions/fetch-or-create-user";
import type { Plan } from "@stigg/react-sdk";
import { UserProvider } from "@/providers/user-provider";
import { api } from "@/trpc/react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useParams, useRouter } from "next/navigation";
import { OrganizationSelector } from "@/components/organization-selector";

export type AppCtxRemoteEndpoint = Pick<
  RemoteEndpoint,
  "id" | "type" | "config" | "schemaVersion"
>;
export type ExtendedProject = Project & {
  remoteEndpoints: AppCtxRemoteEndpoint[];
  copilotCloudPublicApiKeys: Pick<
    CopilotCloudPublicApiKey,
    "key" | "createdAt" | "allowedDomains" | "isDomainLocked"
  >[];
  copilotCloudPrivateApiKeys: Pick<CopilotCloudPrivateApiKey, "key">[];
  providerApiKeys: Pick<ProviderApiKey, "provider" | "censoredValue">[];
};

interface AppContextType {
  user: ExtendedUser | null;
  projects: ExtendedProject[];
  currentProject: ExtendedProject | null;
  currentPlan: Plan | null;
  organizationId?: string;
  organizations: Array<{
    id: string;
    name: string;
    clerkInvitationOrgId: string | null;
  }>;
  // Loading and error states are removed as data is provided via props
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({
  children,
  context,
}: {
  children: ReactNode | ReactNode[];
  context: Omit<
    AppContextType,
    | "setProjects"
    | "setCurrentProject"
    | "currentProject"
    | "projects"
    | "organizations"
  >;
}) {
  // Always call hooks in the same order - critical for hooks rules
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const router = useRouter();
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [showOrgSelector, setShowOrgSelector] = useState(false);
  const [shouldRedirectToInvite, setShouldRedirectToInvite] = useState(false);
  const mountedRef = useRef(false);

  // Destructure props after hooks to maintain hook order
  const { user, currentPlan } = context;

  // Fetch user organizations for the sidebar selector
  const { data: userOrganizations } = api.organizations.getUserOrgs.useQuery(
    undefined,
    { enabled: !!user },
  );

  const {
    data: projects,
    isLoading: loadingProjects,
    error,
  } = api.projects.getAllExtended.useQuery(undefined, {
    enabled: !!user,
    retry: (failureCount, error) => {
      // Check for no memberships - set flag for redirect
      if (
        error?.data?.code === "UNAUTHORIZED" &&
        error?.message === "NO_MEMBERSHIPS_REDIRECT_TO_ACCEPT"
      ) {
        setShouldRedirectToInvite(true);
        return false; // Don't retry
      }

      // Check for multiple organizations error
      if (error?.data?.code === "BAD_REQUEST") {
        try {
          const parsed = JSON.parse(error.message);
          if (parsed.type === "MULTIPLE_ORGANIZATIONS") {
            setOrganizations(parsed.organizations);
            setShowOrgSelector(true);
            return false; // Don't retry
          }
        } catch (e) {
          // Not a multiple orgs error
        }
      }

      return failureCount < 3;
    },
  });

  // Track component mounting to prevent hydration issues
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Handle navigation outside of render cycle
  useEffect(() => {
    if (shouldRedirectToInvite && mountedRef.current) {
      router.push("/invitation/accept");
      setShouldRedirectToInvite(false);
    }
  }, [shouldRedirectToInvite, router]);

  const currentProject =
    useMemo(() => {
      if (!projectIdParam && projects?.length) return projects[0];
      return projects?.find((project) => project.id === projectIdParam);
    }, [projectIdParam, projects]) || null;

  // Render content based on state, but avoid early returns that break hooks order
  let content;

  if (showOrgSelector && organizations.length > 1) {
    content = (
      <OrganizationSelector
        organizations={organizations}
        open={showOrgSelector}
        onClose={() => setShowOrgSelector(false)}
      />
    );
  } else if (user && loadingProjects) {
    content = (
      <div
        id="loading-spinner-container"
        className="fixed inset-0 z-50 flex items-center justify-center bg-white transition-opacity duration-300"
      >
        <LoadingSpinner />
      </div>
    );
  } else if (user && !projects?.length && !error) {
    content = <div>Error retrieving projects</div>;
  } else {
    content = (
      <UserProvider
        context={{
          user: context.user,
          project: currentProject,
        }}
      >
        {children}
      </UserProvider>
    );
  }

  return (
    <AppContext.Provider
      value={{
        user,
        projects: projects ?? [],
        currentProject: currentProject,
        currentPlan,
        organizationId: user?.organization.id,
        organizations: userOrganizations ?? [],
      }}
    >
      {content}
    </AppContext.Provider>
  );
}

export function useAppContext() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
}
